import classes from "../../../styles/FuelLubricantsModal.module.scss";
import Loader from "components/Loader/Loader";
import { useCallback, useEffect, useState } from "react";
import { FormatDate, FormatDateIso } from "utils/dateUtils";
import { IRefuelResourceRequestParams } from "Dashboard/scenes/Resources/domain/IRefuelResourceRequestParams";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { useSelector } from "react-redux";
import {
  addRefuel,
  clearFuelMap,
  getConsumables,
  selectState,
  setFuelLuricantsPopover,
  setFuelMap,
} from "../fuelsAndLubricants.slice";
import {
  selectResourceDetails,
  updateAverageConsumption,
  updateLastKnownUsageAmount
} from "../../Details/details.slice";
import { useAppDispatch } from "hooks/hooks";
import { IConsumableItem } from "Dashboard/scenes/Resources/domain/IConsumableItem";
import { toast } from "react-toastify";
import { IInput } from "Dashboard/scenes/Consumables/domain/IInput";
import classesPop from "../../../../../../components/Popover/popover.module.scss";
import { Controller, useForm } from "react-hook-form";
import { haveUsage, unitName, unitNameLabel } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import InfoTooltip from "components/Popup/InfoTooltip";
import { copyToClipboard } from "utils/clipboardUtils";
import NumberInput from "Dashboard/components/NumberInput";
import DatePicker from "react-datepicker";

const FuelLubricantsPopover = () => {
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<IRefuelResourceRequestParams>();

  const {
    consumablesLoading,
    consumables,
    fuelMap } = useSelector(selectState);

  const resourceDetails = useSelector(selectResourceDetails);
  const [clickedTimes, setClickedTimes] = useState(0);
  const [emptyInputsWarning, setEmptyInputsWarning] = useState<boolean>(false);
  const [lowUsage, setLowUsage] = useState<boolean>(false);
  const [currentUsage, setCurrentUsage] = useState<number>(0);
  const [chargedOnProject, setChargedOnProject] = useState<string | undefined>(undefined);
  const [currentChargeId, setCurrentChargeId] = useState<number | undefined>(undefined);

  useEffect(() => {
    setClickedTimes(0);
    dispatch(getConsumables());
  }, [dispatch]);

  useEffect(() => {
    if (resourceDetails !== undefined && resourceDetails.currentCharges.length > 0) {
      setChargedOnProject(resourceDetails.currentCharges[0].project.name);
      setCurrentChargeId(resourceDetails.currentCharges[0].chargeId);
    }
  }, [resourceDetails, resourceDetails?.currentCharges]);

  const close = () => {
    dispatch(setFuelLuricantsPopover({ isOpen: false }));
    dispatch(clearFuelMap());
  };

  const onChange =
    (consumableInputId: number) => (newValue: number | undefined) => {
      dispatch(
        setFuelMap({
          consumableInputId: consumableInputId,
          quantityAmount: newValue,
        })
      );
    };

  const onChangeUsage = (newUsage: number) => {
    setCurrentUsage(newUsage);
    if (resourceDetails && clickedTimes > 0) {
      setLowUsage(newUsage <= resourceDetails?.resource.lastKnownUsageAmount);
    }
  }

  const getConsumableItems = useCallback(() => {
    const consumableItems: IConsumableItem[] = [];
    for (const [key, value] of Object.entries(fuelMap)) {
      consumableItems.push({
        consumableInputId: Number(key),
        quantityAmount: value || 0,
      });
    }
    return consumableItems;
  }, [fuelMap]);

  const handleInputBlur = (
    newValue: number,
    consumable: IInput
  ) => {
    const inputValue = newValue;
    const remainingValue = consumable.quantityAmountRemaining || 0;
    let finalValue: number | undefined = inputValue;

    if (inputValue > remainingValue) {
      finalValue = remainingValue > 0 ? remainingValue : undefined;
      const overflowValue = Math.round((inputValue - remainingValue) * 100) / 100.0;
      toast.info(
        `Nema ${inputValue} na lageru. Upisujem ${remainingValue}, a nedostajućih ${overflowValue} upisujem u klipboard.`
      );
      copyToClipboard((overflowValue).toString());
    }
    onChange(consumable.inputId)(finalValue);
  };

  const firstClick = () => {
    setClickedTimes(clickedTimes + 1);
    const items = getConsumableItems();
    items.length <= 0
      ? setEmptyInputsWarning(true) : setEmptyInputsWarning(false);
    if (resourceDetails) {
      if (currentUsage <= resourceDetails?.resource.lastKnownUsageAmount) {
        setLowUsage(true);
      }
    }
  }

  const onSubmit = async (data: IRefuelResourceRequestParams) => {
    const items = getConsumableItems();

    if (resourceDetails) {
      if (resourceDetails?.resource.lastKnownUsageAmount < currentUsage) {
        setLowUsage(true);
        toast.error(`Unesite za '${unitNameLabel(resourceDetails?.resource.usageUnit)}' manju ili jednaku vrednost poslednjoj unetoj (${FormatNumberWithUnitOrNull(resourceDetails?.resource.lastKnownUsageAmount, resourceDetails?.resource.usageUnit.name)})!`);
        return;
      }
    }

    if (resourceDetails && items.length > 0) {
      try {
        data = {
          ...data,
          consumables: items,
          chargeId: currentChargeId,
        }

        const refuelResponse = await dispatch(addRefuel({
          resourceId: resourceDetails?.resource.resourceId, data
        })).unwrap();
        dispatch(updateLastKnownUsageAmount(data.usageAmount));
        if (refuelResponse.averageConsumption) {
          dispatch(updateAverageConsumption(refuelResponse.averageConsumption));
        }
        toast.success("Unos goriva/maziva je uspešno dodat.");
        reset();
        close();
      } catch (response: any) {
        console.log(response);
        if (response.status === 403) {
          if (response.data.message === "Not an employee") {
            toast.error("Nije dozvoljen unos goriva i maziva  (da li ste zaposleni u firmi?)");
            return;
          }
        } else if (response.status === 400) {
          toast.error("Neočekivana greška, pokušajte da osvežite stranicu pa pokušajte ponovo");
          return;
        }
        toast.error("Neočekivana greška, pokušajte da osvežite stranicu pa pokušajte ponovo");
      }
    }
  };

  useEffect(() => {
    if (clickedTimes > 0) {
      const items = getConsumableItems();
      items.length <= 0
        ? setEmptyInputsWarning(true) : setEmptyInputsWarning(false);
    }
  }, [clickedTimes, getConsumableItems])

  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`${classes.modalContent}`}>
      <div>
        <div className={`${classes.content}`}>
          <div className={classes.table}>
            <div className={`g-grid g-header ${classes.gHeader}`}>
              <div className={`row ${classes.row}`}>
                <div className="col center lowercase separator-line">
                  br. otpr. rač.</div>
                <div className="col center lowercase separator-line">
                  naziv</div>
                <div className="col center lowercase separator-line">
                  količina</div>
                <div className="col center lowercase separator-line">
                  datum ulaza</div>
                <div className="col center lowercase separator-line">
                  kol. ostalo</div>
                <div className="col center uppercase">
                  IZDAJEM</div>
                <div></div>
              </div>
            </div>
            <div
              className={`g-grid g-content ${classes.gContent} loading-container`}
            >
              {consumablesLoading && <Loader />}
              {consumables.map((consumable, index) => (
                <div className={`row ${classes.row}`} key={index}>
                  <div className={`col ${classes.col} capitalize light`}>
                    <div className="bg-item overflowMinWidth right">
                      <InfoTooltip
                        classesProp="bg-text-overflow"
                        tooltip={consumable.billOfLandingId.toString()}>
                        {consumable.billOfLandingId}
                      </InfoTooltip>
                    </div>
                  </div>
                  <div className={`col ${classes.col} light`}>
                    <div className="bg-item overflowMinWidth center">
                      <InfoTooltip
                        classesProp={"bg-text-overflow"}
                        tooltip={`${consumable.item.name} - ${consumable.supplier.shortName}`}>
                        {consumable.item.name}
                      </InfoTooltip>
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize light`}>
                    <div className="bg-item overflowMinWidth right">
                      <InfoTooltip
                        classesProp={"bg-text-overflow"}
                        tooltip={`${FormatNumber(consumable.quantityAmountTotal)}${" L"}`}>
                        {FormatNumber(consumable.quantityAmountTotal)}{" L"}
                      </InfoTooltip>
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize light center`}>
                    <div className={`bg-item center`}>
                      {FormatDate(consumable.date)}
                    </div>
                  </div>
                  <div className={`col ${classes.col} capitalize strong center`}>
                    <div className={`bg-item center`}>
                      {FormatNumber(consumable.quantityAmountRemaining)}
                    </div>
                  </div>
                  <div className={`col ${classes.col} uppercase light center`}>
                    <NumberInput
                      formatAs="quantity"
                      value={fuelMap[consumable.inputId]?.toString() || ""}
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        onChange(consumable.inputId)(parsedValue);
                      }}
                      onBlur={(newValue: number) => handleInputBlur(newValue, consumable)}
                      className={`bg-item-dark center
                      ${emptyInputsWarning ? classes.bgItemError : ""}`}
                    />
                  </div>
                  <div className={classes.check}>
                    {fuelMap[consumable.inputId] !== undefined &&
                      consumable.quantityAmountRemaining !== undefined &&
                      fuelMap[consumable.inputId]! <=
                      consumable.quantityAmountRemaining && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={`${classes.options} ${classesPop.basicPopover}`}>
            {resourceDetails && !haveUsage(resourceDetails.resource.category) && (
              <div className={`${classesPop.row} ${classesPop.rowDir} ${classes.projectRow}`}>
                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}>za koji projekat</label>
                  <input
                    type="text"
                    className={`${classesPop.itemBorder} ${classesPop.center}
                    ${classesPop.textMedium} ${classesPop.transparent}`}
                    value={chargedOnProject}
                    disabled />
                </div>

                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}>datum</label>
                  <Controller
                    control={control}
                    name="date"
                    defaultValue={FormatDateIso(new Date())}
                    rules={{ required: false, }}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <DatePicker
                          onChange={(date) =>
                            onChange(date ? FormatDateIso(date) : undefined)
                          }
                          dateFormat="dd.MM.yyyy"
                          showMonthDropdown
                          showYearDropdown
                          selected={value ? new Date(value) : null}
                          className={`${classesPop.dateItem} ${classesPop.itemBorder}
                            ${classes.datePicker}
                            ${errors.date ? classesPop.itemError : ""}`}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            )}
            {resourceDetails && haveUsage(resourceDetails.resource.category) && (
              <div className={`${classesPop.row} ${classesPop.rowDir} ${classes.usageRow}`}>
                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}>jed mere</label>
                  <input
                    type="text"
                    className={`${classesPop.itemBorder} ${classesPop.center}
                    ${classesPop.textMedium} ${classesPop.transparent}`}
                    value={resourceDetails &&
                      unitName(resourceDetails?.resource.usageUnit)}
                    disabled />
                </div>
                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}
                  >{resourceDetails &&
                    unitNameLabel(resourceDetails?.resource.usageUnit)}
                  </label>
                  <Controller
                    name="usageAmount"
                    control={control}
                    defaultValue={undefined}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange } }) => (
                      <NumberInput
                        formatAs="quantity"
                        onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                          onChange({ target: { value: parsedValue } });
                          onChangeUsage(parsedValue);
                        }}
                        placeholder={resourceDetails?.resource.lastKnownUsageAmount !== undefined
                          ? FormatNumber(resourceDetails.resource.lastKnownUsageAmount)
                          : undefined}
                        className={`${classesPop.itemBorder} ${classesPop.center}
                                    ${errors.usageAmount || lowUsage === true
                            ? classesPop.itemError : ""}`}
                      />
                    )}
                  />
                </div>

                <div className={`${classesPop.col} ${classesPop.colDir}`}>
                  <label className={classesPop.aboveLabel}>datum</label>
                  <Controller
                    control={control}
                    name="date"
                    defaultValue={FormatDateIso(new Date())}
                    rules={{ required: false, }}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <DatePicker
                          onChange={(date) =>
                            onChange(date ? FormatDateIso(date) : undefined)
                          }
                          dateFormat="dd.MM.yyyy"
                          showMonthDropdown
                          showYearDropdown
                          selected={value ? new Date(value) : null}
                          className={`${classesPop.dateItem} ${classesPop.itemBorder}
                            ${classes.datePicker}
                            ${errors.date ? classesPop.itemError : ""}`}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className={classesPop.col}>
                  <label>sipao pun</label>
                  <div className={classes.checkBorder}>
                    <input
                      type="checkbox"
                      {...register("isFullTank")}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={`${classesPop.row} ${classesPop.rowDir} ${classes.noteRow}`}>
              <div className={`${classesPop.col} ${classesPop.colDir}`}>
                <label className={classesPop.aboveLabel}>napomena</label>
                <input type="text" className={classesPop.itemBorder}
                  {...register("note")} />
              </div>

              <div className={`${classesPop.buttons} ${classesPop.bottom}`}>
                <button type="submit"
                  onClick={() => clickedTimes === 0
                    ? firstClick() : null}
                  className={classesPop.iconButton}>
                  <FontAwesomeIcon
                    icon={faLongArrowAltDown}
                    className={classesPop.icon} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form >
  );
};

export default FuelLubricantsPopover;
